import { unref, type MaybeRef } from 'vue';

import { useMergedPaginatedFeed, type MergedPaginatedFeedSource } from '@peanuds/composables';
import {
  createGenericTransactionsRequestTemplate,
  createIncomingTransfersRequestTemplate,
  createOutgoingTransfersRequestTemplate,
  type ListGenericTransactionsData,
  type ListIncomingTransfersData,
  type ListOutgoingTransfersData,
} from '@peanuds/shared';
import {
  mapGenericTransfers,
  mapIncomingTransfers,
  mapOutgoingTransfers,
} from './transactions.boundary';
import { normalizeAccountIds } from './transactions.account-ids';
import {
  createGenericTransactionsOptions,
  createIncomingTransfersOptions,
  createOutgoingTransfersOptions,
} from './transactions.request-options';
import type { Transaction, TransactionDirection } from './transactions.types';

type TransactionsFeedFetchOptions = {
  transferDateFrom?: MaybeRef<string | undefined>;
  transferDateTo?: MaybeRef<string | undefined>;
  incomingQuery?: MaybeRef<Partial<ListIncomingTransfersData['query']> | undefined>;
  outgoingQuery?: MaybeRef<Partial<ListOutgoingTransfersData['query']> | undefined>;
  genericQuery?: MaybeRef<Partial<ListGenericTransactionsData['query']> | undefined>;
  includeGenericTransactions?: MaybeRef<boolean>;
};

export type UsePaginatedTransactionsFeedOptions = TransactionsFeedFetchOptions & {
  accountIds: MaybeRef<readonly string[]>;
  directions?: MaybeRef<readonly TransactionDirection[]>;
  pageSize: number;
  sourcePageSize?: number;
};

const getTransactionKey = (transaction: Transaction) => {
  const transactionIdentifier =
    transaction.id ||
    ('transactionNumber' in transaction && typeof transaction.transactionNumber === 'string'
      ? transaction.transactionNumber
      : undefined);
  const fallbackKey = `${transaction.transferDate}-${transaction.amount}`;
  const source = transaction.transactionSource ?? transaction.type;

  return `${source}:${transaction.direction}:${transactionIdentifier || fallbackKey}`;
};

const getTransactionSortValue = (transaction: Transaction) => {
  const timestamp = Date.parse(transaction.transferDate ?? '');

  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const compareTransactionsByTransferDateDesc = (left: Transaction, right: Transaction) => {
  const sortDifference = getTransactionSortValue(right) - getTransactionSortValue(left);

  if (sortDifference !== 0) {
    return sortDifference;
  }

  return getTransactionKey(left).localeCompare(getTransactionKey(right));
};

const getTransactionFeedSdkResponseError = (response: unknown): unknown | undefined => {
  if (typeof response !== 'object' || response === null || !('error' in response)) {
    return undefined;
  }

  return (response as { error?: unknown }).error;
};

const unwrapTransactionDirections = (
  directions: MaybeRef<readonly TransactionDirection[]> | undefined,
): readonly TransactionDirection[] => {
  return unref(directions) ?? ['in', 'out'];
};

export const usePaginatedTransactionsFeed = ({
  accountIds,
  directions,
  transferDateFrom,
  transferDateTo,
  incomingQuery,
  outgoingQuery,
  genericQuery,
  includeGenericTransactions = false,
  pageSize,
  sourcePageSize,
}: UsePaginatedTransactionsFeedOptions) => {
  const incomingTransfersRequest = createIncomingTransfersRequestTemplate();
  const outgoingTransfersRequest = createOutgoingTransfersRequestTemplate();
  const genericTransactionsRequest = createGenericTransactionsRequestTemplate();

  const createTransactionFeedSources = (): MergedPaginatedFeedSource<Transaction>[] => {
    const normalizedAccountIds = normalizeAccountIds([...unref(accountIds)]);
    const resolvedDirections = unwrapTransactionDirections(directions);
    const resolvedTransferDateFrom = unref(transferDateFrom);
    const resolvedTransferDateTo = unref(transferDateTo);
    const resolvedIncomingQuery = unref(incomingQuery);
    const resolvedOutgoingQuery = unref(outgoingQuery);
    const resolvedGenericQuery = unref(genericQuery);
    const shouldIncludeGenericTransactions = unref(includeGenericTransactions);
    const transactionSources: MergedPaginatedFeedSource<Transaction>[] = [];

    for (const accountId of normalizedAccountIds) {
      if (resolvedDirections.includes('in')) {
        transactionSources.push({
          id: `incoming:${accountId}`,
          fetchPage: async ({ limit, offset }) => {
            const response = await incomingTransfersRequest.request(
              createIncomingTransfersOptions({
                accountId,
                transferDateFrom: resolvedTransferDateFrom,
                transferDateTo: resolvedTransferDateTo,
                limit,
                offset,
                incomingQuery: resolvedIncomingQuery,
              }),
            );

            if (response instanceof Error) {
              throw response;
            }

            const responseError = getTransactionFeedSdkResponseError(response);

            if (responseError !== undefined) {
              throw responseError;
            }

            return mapIncomingTransfers(response.data.data ?? []);
          },
        });
      }

      if (resolvedDirections.includes('out')) {
        transactionSources.push({
          id: `outgoing:${accountId}`,
          fetchPage: async ({ limit, offset }) => {
            const response = await outgoingTransfersRequest.request(
              createOutgoingTransfersOptions({
                accountId,
                transferDateFrom: resolvedTransferDateFrom,
                transferDateTo: resolvedTransferDateTo,
                limit,
                offset,
                outgoingQuery: resolvedOutgoingQuery,
              }),
            );

            if (response instanceof Error) {
              throw response;
            }

            const responseError = getTransactionFeedSdkResponseError(response);

            if (responseError !== undefined) {
              throw responseError;
            }

            return mapOutgoingTransfers(response.data.data ?? []);
          },
        });
      }

      if (shouldIncludeGenericTransactions && resolvedDirections.length > 0) {
        let genericOffset = 0;

        transactionSources.push({
          id: `generic:${accountId}:${resolvedDirections.join(',')}`,
          fetchPage: async ({ limit }) => {
            const genericTransactions: Transaction[] = [];

            while (genericTransactions.length < limit) {
              const remainingLimit = limit - genericTransactions.length;
              const response = await genericTransactionsRequest.request(
                createGenericTransactionsOptions({
                  accountId,
                  transferDateFrom: resolvedTransferDateFrom,
                  transferDateTo: resolvedTransferDateTo,
                  limit: remainingLimit,
                  offset: genericOffset,
                  genericQuery: resolvedGenericQuery,
                }),
              );

              if (response instanceof Error) {
                throw response;
              }

              const responseError = getTransactionFeedSdkResponseError(response);

              if (responseError !== undefined) {
                throw responseError;
              }

              const rawGenericTransactions = response.data.data ?? [];
              const matchingGenericTransactions = mapGenericTransfers(
                rawGenericTransactions,
              ).filter((transaction) => {
                return resolvedDirections.includes(transaction.direction);
              });

              genericTransactions.push(...matchingGenericTransactions);
              genericOffset += rawGenericTransactions.length;

              if (rawGenericTransactions.length < remainingLimit) {
                break;
              }
            }

            return genericTransactions;
          },
        });
      }
    }

    return transactionSources;
  };

  return useMergedPaginatedFeed<Transaction>({
    sources: createTransactionFeedSources,
    pageSize,
    sourcePageSize,
    compareItems: compareTransactionsByTransferDateDesc,
    getItemKey: getTransactionKey,
  });
};
